import { useState, useRef, TouchEvent as ReactTouchEvent } from 'react';

interface PinchZoomState {
  scale: number;
  translateX: number;
  translateY: number;
}

interface TouchPoint {
  clientX: number;
  clientY: number;
}

export function usePinchZoom(minScale = 1, maxScale = 4) {
  const [state, setState] = useState<PinchZoomState>({
    scale: 1,
    translateX: 0,
    translateY: 0,
  });
  
  const initialDistance = useRef<number | null>(null);
  const initialScale = useRef(1);
  const lastTouchCenter = useRef<{ x: number; y: number } | null>(null);
  const lastTranslate = useRef({ x: 0, y: 0 });

  const getDistance = (touch1: TouchPoint, touch2: TouchPoint) => {
    return Math.hypot(
      touch2.clientX - touch1.clientX,
      touch2.clientY - touch1.clientY
    );
  };

  const getTouchCenter = (touch1: TouchPoint, touch2: TouchPoint) => ({
    x: (touch1.clientX + touch2.clientX) / 2,
    y: (touch1.clientY + touch2.clientY) / 2,
  });


  const handleTouchStart = (e: ReactTouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const t0 = { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
      const t1 = { clientX: e.touches[1].clientX, clientY: e.touches[1].clientY };
      initialDistance.current = getDistance(t0, t1);
      initialScale.current = state.scale;
      lastTouchCenter.current = getTouchCenter(t0, t1);
      lastTranslate.current = { x: state.translateX, y: state.translateY };
    }
  };

  const handleTouchMove = (e: ReactTouchEvent) => {
    if (e.touches.length === 2 && initialDistance.current !== null) {
      e.preventDefault();
      
      const t0 = { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
      const t1 = { clientX: e.touches[1].clientX, clientY: e.touches[1].clientY };
      const currentDistance = getDistance(t0, t1);
      const currentCenter = getTouchCenter(t0, t1);
      
      // Calculate new scale
      const scaleChange = currentDistance / initialDistance.current;
      let newScale = initialScale.current * scaleChange;
      newScale = Math.max(minScale, Math.min(maxScale, newScale));
      
      // Calculate pan
      let newTranslateX = state.translateX;
      let newTranslateY = state.translateY;
      
      if (lastTouchCenter.current && newScale > 1) {
        const deltaX = currentCenter.x - lastTouchCenter.current.x;
        const deltaY = currentCenter.y - lastTouchCenter.current.y;
        newTranslateX = lastTranslate.current.x + deltaX;
        newTranslateY = lastTranslate.current.y + deltaY;
      }
      
      setState({
        scale: newScale,
        translateX: newScale > 1 ? newTranslateX : 0,
        translateY: newScale > 1 ? newTranslateY : 0,
      });
    }
  };

  const handleTouchEnd = () => {
    initialDistance.current = null;
    lastTouchCenter.current = null;
    lastTranslate.current = { x: state.translateX, y: state.translateY };
    
    // Reset if scale is close to 1
    if (state.scale <= 1.1) {
      setState({ scale: 1, translateX: 0, translateY: 0 });
    }
  };

  const resetZoom = () => {
    setState({ scale: 1, translateX: 0, translateY: 0 });
  };

  const toggleZoom = () => {
    if (state.scale > 1) {
      resetZoom();
    } else {
      setState({ scale: 2, translateX: 0, translateY: 0 });
    }
  };

  return {
    scale: state.scale,
    translateX: state.translateX,
    translateY: state.translateY,
    isZoomed: state.scale > 1,
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    resetZoom,
    toggleZoom,
  };
}
